import { useContext } from "@nuxtjs/composition-api"
import getSymbolFromCurrency from "currency-symbol-map"

import currencyFeesQuery from "@/api/query/currencyFeesQuery"
import { Constants, CurrencyId } from "@/enums"
import { numberToFixed } from "@/utils/misc"

function useCurrency() {
  const { i18n } = useContext()

  const { data: currencyFees } = currencyFeesQuery()
  const getCurrencyFee = (currencyId: CurrencyId) => {
    return currencyFees?.value?.filter(
      (currencyFee) => currencyFee.id === currencyId,
    )[0]
  }

  const formatCurrency = (
    currency: string = CurrencyId.EUR,
    number: number,
  ) => {
    return new Intl.NumberFormat(i18n.locale, {
      style: "currency",
      currency,
      minimumFractionDigits: 0,
    }).format(number)
  }

  const getCurrencySymbol = (currency: CurrencyId | undefined): string => {
    if (currency) {
      return getSymbolFromCurrency(currency) || ""
    }
    return ""
  }

  const chargingFeeCalc = () => {
    const ourFee = (value: number, currencyId: CurrencyId) => {
      if (value <= 0) {
        return 0
      }

      const currencyFee = getCurrencyFee(currencyId)!!
      return numberToFixed(
        Math.max(
          currencyFee.minPlatformFee,
          (currencyFee.percentagePlatformFee * value) / 100,
        ),
        2,
      )
    }

    const yourFee = (value: number, currency: CurrencyId) =>
      numberToFixed(value - Number(ourFee(value, currency)), 2)

    const thresholdFee = (currencyId: CurrencyId) => {
      const currencyFee = getCurrencyFee(currencyId)!!
      return Math.ceil(
        currencyFee.minPlatformFee / (currencyFee.percentagePlatformFee / 100),
      )
    }

    const subscriptionOurFee = (value: number) => {
      return numberToFixed(
        (Constants.SUBSCRIPTION_FEE_PERCENTAGE / 100) * value,
        2,
      )
    }

    const subscriptionYourFee = (value: number) => {
      return numberToFixed(Number(value) - Number(subscriptionOurFee(value)), 2)
    }

    return {
      ourFee,
      yourFee,
      thresholdFee,
      subscriptionOurFee,
      subscriptionYourFee,
    }
  }

  return {
    getCurrencyFee,
    formatCurrency,
    getCurrencySymbol,
    chargingFeeCalc,
  }
}

export default useCurrency
